import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './view-page.vue';
import {
   rescoverApi,
   SentryPlugin,
   formatters,
   PrimeVueConfig as PrimeVue,
   type VueGoogleMapsOptions,
   VueGoogleMapsPlugin,
   PermissionsViewer
} from '@rescover/ui-library';
import router from '~/router';
import Aura from '@primeuix/themes/aura';
import enums from 'vue-enums';

const appElementId = 'property-view-app';
const componentDebug = false;
/*
 * This contains 'listing' code that is solely intended to only be on a listing page.
 * 
 * Please include short description above each .on() statement.
 * KEEP THIS SCRIPT DOCUMENT CLEAN!
 */

declare global {
   interface Window {
      analysis?: string
      share?: string
      client?: number
      clientId?: number
      googleApi?: string
   }
}

/*
 * Force authentication check
 */
if (window.client)
   rescoverApi.api.clientId = window.client;

if (window.analysis ?? window.share)
   rescoverApi.apiKey = window.analysis ?? window.share;

const pinia = createPinia();

const app = createApp(App)
   .component('PermissionsViewer', PermissionsViewer)
   .use(enums)
   .use(SentryPlugin, {
      enabled: import.meta.env.PROD,
      release: import.meta.env.VITE_SENTRY_RELEASE,
   })
   .use(pinia)
   .use(VueGoogleMapsPlugin, {
      load: {
         key: window.googleApi,
         libraries: ['drawing', 'marker', 'geometry'],
         v: 'beta'
      },
   } as VueGoogleMapsOptions)
   .use(router)
   .use(PrimeVue, {
      theme: {
         preset: Aura,
         options: {
            prefix: 'p',
            darkModeSelector: '.app-dark',
            cssLayer: false
         }
      }
   })
   .directive('hasChanged', {
      updated(el: HTMLInputElement, binding) {
         let value = binding.instance;
         let orig = binding.instance;
         const path = binding.value.split('.');
         path.forEach((prop, idx) => {
            value = value[prop];
            if (orig)
               orig = orig[idx === 0 ? `${prop}Orig` : prop];
         });
         if (value === orig) {
            el.classList.remove('changed');
         } else {
            el.classList.add('changed');
         }
      }
   })
   .directive('wasChanged', {
      updated(el: HTMLInputElement, binding) {
         try {
            const orig = binding.value;
            let value = undefined;
            const _type = typeof binding.value;
            switch (_type) {
               case 'number':
                  value = Number(el.value);
                  break;
               case 'string':
                  value = el.value;
                  break;
               case 'undefined':
                  return;
               default:
                  console.warn(`Unhandled type for was-changed ${_type}`);
                  break;
            }
            if (DEBUG && componentDebug)
               console.log('wasChanged', el as object, binding, _type, value, orig);
            if (value === orig)
               el.classList.remove('changed');
            else
               el.classList.add('changed');
         }
         catch {
            //
         }
      }
   })
   .directive('number', {
      updated(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.number(binding.value);
         }
      },
      beforeMount(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.number(binding.value);
         }
      }
   })
   .directive('numberOne', {
      updated(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.number(binding.value, 1);
         }
      },
      beforeMount(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.number(binding.value, 1);
         }
      }
   })
   .directive('content', {
      // This is to replicate v-text functionality, as v-text gives error when children exists.
      updated(el: HTMLElement, binding) {
         el.textContent = binding.value;
      },
      beforeMount(el: HTMLElement, binding) {
         el.textContent = binding.value;
      }
   })
   .directive('percent', {
      updated(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.percent(binding.value);
         }
      },
      beforeMount(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.percent(binding.value);
         }
      }
   })
   .directive('percentOne', {
      updated(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.percent(binding.value, 1);
         }
      },
      beforeMount(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.percent(binding.value, 1);
         }
      }
   })
   .directive('currency', {
      updated(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.currency(binding.value);
         }
      },
      beforeMount(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.currency(binding.value);
         }
      }
   })
   .directive('currencyCents', {
      updated(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.currency(binding.value, true);
         }
      },
      beforeMount(el: HTMLElement, binding) {
         if (isNaN(binding.value) === true) {
            el.textContent = binding.value;
         } else {
            el.textContent = formatters.currency(binding.value, true);
         }
      }
   });

rescoverApi.api.account.ping()
   .then((response) => {
      if (DEBUG && componentDebug)
         console.log('property-view.api', response);
   }, () => { })
   .finally(() => {
      app.mount(`#${appElementId}`);
   });
